'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { VoiceGender } from '@/contexts/ai/types/voice';

export interface CloneVoiceActionInput {
  name: string;
  description?: string;
  /** Pre-uploaded sample keys (POST /api/voice-samples) */
  sampleUrls: string[];
  voiceEntryId: string;
  familyId: string;
  language?: string;
  /** Speaker gender as told by the user (voice-library metadata). */
  gender?: VoiceGender;
  /** Script the user read (adherence check). */
  scriptText?: string;
}

export async function cloneVoice(
  input: CloneVoiceActionInput,
): Promise<ActionResult<{ voiceEntryId: string; provider: string }>> {
  try {
    const userId = await SessionService.getUserId();
    const familyId = await SessionService.getFamilyId();
    if (!userId || !familyId) return { success: false, error: 'Not authenticated' };

    if (familyId !== input.familyId) {
      return { success: false, error: 'Forbidden' };
    }

    if (!input.name || input.name.trim().length < 2) {
      return { success: false, error: 'Voice name must be at least 2 characters' };
    }

    if (!input.sampleUrls || input.sampleUrls.length === 0) {
      return { success: false, error: 'At least one audio sample is required' };
    }

    if (input.sampleUrls.length > 25) {
      return { success: false, error: 'Maximum 25 audio samples allowed' };
    }

    const voiceEntryId = input.voiceEntryId;
    // Path INSIDE the 'voice-samples' container — the worker prefixes the container
    // on upload; a prefixed path here doubled it (voice-samples/voice-samples/…)
    // and the ownership check then refused every preview (403 → "operation not
    // supported" on the phone).
    const previewBlobPath = `${familyId}/${voiceEntryId}/preview.mp3`;

    const { getAudioGenerationQueue } = await import(
      '@/contexts/ai/infrastructure/audioGenerationQueue'
    );

    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    const { DEFAULT_CLONE_PROVIDER } = await import('@/contexts/ai/types/voice');
    const cfg = await UnifiedAIConfigService.getConfig();
    // 'fake' TTS stand → fake clone too (no spend); else the admin's clone
    // engine. `||`, not `??`: production had cloneProvider saved as an EMPTY
    // STRING, which `??` happily passed through — the job then carried no
    // provider at all and the worker fell back to a client that cannot clone.
    const provider = cfg.tts.provider === 'fake'
      ? 'fake'
      : (cfg.tts.cloneProvider?.trim() || DEFAULT_CLONE_PROVIDER);

    // The clone is a METERED premium op (MiniMax charges per voice): hold the
    // fee on the family's allowance first; the consumer settles it from the
    // worker's usage fact (op_type 'voice-clone', 1 count) — 0 for ElevenLabs.
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { walletAccountId } = await import('@/contexts/subscriptions/services/walletAccountId');
    const tokenManager = getTokenManager();
    const holdAmount = await UnifiedAIConfigService.getOperationReservation('voice_clone');
    const reservation = await tokenManager.reserve({
      familyId,
      accountId: await walletAccountId(familyId),
      amount: holdAmount,
      operationType: 'voice_clone',
      metadata: { voiceEntryId, provider },
    });

    try {
      await getAudioGenerationQueue().enqueue({
        type: 'voice-clone',
        familyId,
        voiceEntryId,
        name: input.name,
        description: input.description,
        sampleUrls: input.sampleUrls,
        language: input.language || 'ru',
        gender: input.gender,
        createdBy: userId,
        previewBlobPath,
        provider,
        scriptText: input.scriptText,
        reservationId: reservation.id,
        modelId: cfg.tts.cloneModel || undefined,
      });
    } catch (err) {
      await tokenManager.refund({ reservationId: reservation.id, reason: 'Voice clone enqueue failed' }).catch(() => {});
      throw err;
    }

    return { success: true, data: { voiceEntryId, provider } };
  } catch (e: unknown) {
    actionErrorLog('cloneVoice', e);
    const message = e instanceof Error ? e.message : 'Failed to clone voice';
    return { success: false, error: message };
  }
}
